import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ToastQueue } from '@adobe/react-spectrum';
import { useHomeSlice } from 'app/pages/Home/slice';
import { EditEventModal } from 'app/components/RsdForms/EditEventModal';
import { updateMoment } from 'app/components/RsdForms/api';
import { STATUS_VALUES } from 'utils/constants';
import {
  deleteMoment,
  clearEventsCache,
  clearParentMomentsCache
} from 'app/services/momentsApi';
import type { MonthlyTimelineItem } from '../utils/types';

export type MonthlyMomentKind = 'key-date' | 'event';

const MOMENT_ACTIONS_CONFIG = {
  'key-date': {
    itemType: 'Key Dates',
    momentType: 'Key Dates',
    completeMessage: 'Key Dates marked as complete successfully!',
    completeError: 'Failed to mark key dates as complete. Please try again.',
    deleteSuccess: 'Key Date has been deleted',
    deleteError: 'Unable to delete Key Date',
    deleteTitle: 'Delete Key Date?',
    deleteMessage:
      'Are you sure you want to delete this key date? This action cannot be undone.',
    deleteConfirmLabel: 'Delete'
  },
  event: {
    itemType: 'Event',
    momentType: 'Event',
    completeMessage: 'Event marked as complete successfully!',
    completeError: 'Failed to mark event as complete. Please try again.',
    deleteSuccess: 'Event has been deleted',
    deleteError: 'Unable to delete Event',
    deleteTitle: 'Delete Event?',
    deleteMessage:
      'Are you sure you want to delete this event? This action cannot be undone.',
    deleteConfirmLabel: 'Delete'
  }
} as const;

function buildEditInitialValues(
  item: MonthlyTimelineItem,
  momentKind: MonthlyMomentKind
) {
  const config = MOMENT_ACTIONS_CONFIG[momentKind];

  return {
    title: item.title || '',
    eventName: item.title || '',
    name: item.title || '',
    projectType: item.projectType || config.itemType,
    momentType: config.momentType,
    startDate: item.dates?.targetStart || null,
    endDate: item.dates?.targetEnd || null,
    scheduleRange: (item as any).scheduleRange || null,
    dateRange: (item as any).dateRange || null,
    dates: item.dates || null,
    description: (item as any).reportingSummary || '',
    id: item.issueId || '',
    categories: (item as any).categories || '',
    products: item.products || '',
    labels: (item as any).labels || [],
    riskLevel: item.riskLevel || '',
    status: item.status || '',
    priority: (item as any).priority || '',
    releasePhase: item.releasePhase || ''
  };
}

export function useMonthlyMomentActions(momentKind: MonthlyMomentKind) {
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  const config = MOMENT_ACTIONS_CONFIG[momentKind];

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMoment, setSelectedMoment] =
    useState<MonthlyTimelineItem | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    variant: 'success' | 'error' | 'info';
  } | null>(null);

  const handleMenuAction = async (
    action: string | number,
    item: MonthlyTimelineItem
  ) => {
    switch (String(action)) {
      case 'edit':
        setSelectedMoment(item);
        setIsEditModalOpen(true);
        break;
      case 'delete':
        setSelectedMoment(item);
        setShowDeleteConfirmation(true);
        break;
      case 'markComplete':
        await handleMarkAsComplete(item);
        break;
      default:
        break;
    }
  };

  const handleMarkAsComplete = async (item: MonthlyTimelineItem) => {
    if (!item.issueId) return;

    try {
      await updateMoment(item.issueId, {
        status: STATUS_VALUES.COMPLETE
      });

      dispatch(actions.loadReleaseData({}));
      setNotification({
        message: config.completeMessage,
        variant: 'success'
      });
    } catch (error: any) {
      console.error('Error marking as complete:', error);
      setNotification({
        message: error?.message || config.completeError,
        variant: 'error'
      });
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedMoment(null);
  };

  const handleEditSuccess = () => {
    dispatch(actions.loadReleaseData({}));
  };

  const handleConfirmDelete = async () => {
    if (!selectedMoment?.issueId) {
      ToastQueue.negative('Unable to delete: Missing item ID', {
        timeout: 5000
      });
      setShowDeleteConfirmation(false);
      return;
    }

    setShowDeleteConfirmation(false);

    try {
      const result = await deleteMoment(selectedMoment.issueId);

      if (result.success && result.status === 204) {
        ToastQueue.positive(config.deleteSuccess, { timeout: 5000 });
        clearEventsCache();
        clearParentMomentsCache();
        dispatch(actions.loadReleaseData({}));
        setSelectedMoment(null);
      } else {
        ToastQueue.negative(config.deleteError, { timeout: 5000 });
        console.error('Delete failed:', result.error || result.message);
      }
    } catch (error) {
      ToastQueue.negative(config.deleteError, { timeout: 5000 });
      console.error('Error deleting moment:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setSelectedMoment(null);
  };

  const editInitialValues = selectedMoment
    ? buildEditInitialValues(selectedMoment, momentKind)
    : null;

  return {
    notification,
    setNotification,
    selectedMoment,
    isEditModalOpen,
    showDeleteConfirmation,
    handleMenuAction,
    handleCloseEditModal,
    handleEditSuccess,
    handleConfirmDelete,
    handleCancelDelete,
    editInitialValues,
    itemType: config.itemType,
    deleteTitle: config.deleteTitle,
    deleteMessage: config.deleteMessage,
    deleteConfirmLabel: config.deleteConfirmLabel,
    EditEventModal
  };
}
